/**
 * User List Component
 *
 * Table displaying all users with their subscription information
 * Includes filters, search, and pagination
 *
 * T083: Create user-list.ts - Table with filters, search, pagination, view/edit buttons
 * T085: Wire up listUsers callable
 */

import { functions } from '@/core/firebase'
import { httpsCallable } from 'firebase/functions'
import { showSubscriptionEditor } from './subscription-editor'
import { showManageBranchesModal } from './branch-manager'

/**
 * User data structure from listUsers callable
 */
export interface UserData {
  uid: string
  email: string | null
  displayName: string | null
  disabled: boolean
  emailVerified: boolean
  createdAt: string
  lastSignInAt: string | null
  customClaims: Record<string, any>
  planType: string
  accounts: { id: string, [key: string]: any }[]
  currentSubscription: any
  subscriptionCount: number
  payAt: string | null
  branchStats: {
    total: number
    renamed: number
  }
}

interface ListUsersResponse {
  users: UserData[]
  nextPageToken: string | null
  totalCount: number
}

// State
let currentPageToken: string | null = null
let currentUsers: UserData[] = []

// --- [THIS IS THE FIX] ---
/**
 * Displays a modal with the generated code, pre-selected for easy copying.
 */
function showGeneratedCodeModal(code: string, expiresAt: string): void {
  // Remove any existing modal
  document.getElementById('generated-code-modal')?.remove()

  const expiryDate = new Date(expiresAt)
  const now = new Date()
  let expiryMessage = 'Expires soon.'

  // Calculate remaining time
  const expiresInMs = expiryDate.getTime() - now.getTime()
  if (expiresInMs > 0) {
    const expiresInMinutes = Math.floor(expiresInMs / 60000)
    const expiresInSeconds = Math.floor((expiresInMs % 60000) / 1000)
    expiryMessage = `This code is valid for ${expiresInMinutes}m ${expiresInSeconds}s.`
  }

  const backdrop = document.createElement('div')
  backdrop.id = 'generated-code-modal'
  backdrop.className =
    'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  backdrop.style.backdropFilter = 'blur(4px)'

  const modal = document.createElement('div')
  modal.className =
    'bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden'

  modal.innerHTML = `
    <div class="p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-2xl font-bold text-gray-900">Code Generated Successfully</h2>
        <button id="close-code-modal" class="text-gray-400 hover:text-gray-600 text-3xl">&times;</button>
      </div>
      <div class="mb-4">
        <p class="text-sm text-gray-600 mb-2">
          Send this one-time code to the user. It is pre-selected for you to copy (Ctrl+C).
        </p>
        <input
          type="text"
          id="generated-code-input"
          value="${code}"
          readonly
          class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-center text-lg"
        />
        <p class="text-xs text-orange-600 text-center mt-2">${expiryMessage}</p>
      </div>
      <div class="flex justify-end">
        <button id="close-code-modal-btn" class="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700">
          Close
        </button>
      </div>
    </div>
  `

  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  const closeModal = () => backdrop.remove()
  modal
    .querySelector('#close-code-modal')
    ?.addEventListener('click', closeModal)
  modal
    .querySelector('#close-code-modal-btn')
    ?.addEventListener('click', closeModal)
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closeModal()
  })

  // Auto-select the text
  const codeInput = modal.querySelector(
    '#generated-code-input',
  ) as HTMLInputElement
  if (codeInput) {
    codeInput.focus()
    codeInput.select()
  }
}
// --- [END FIX] ---

function getNextBillingDetails(
  payAt: string | null,
): { nextBillingText: string; nextBillingClass: string } {
  if (!payAt) {
    return { nextBillingText: '—', nextBillingClass: 'text-gray-500' }
  }

  const lastPayment = new Date(payAt)
  if (Number.isNaN(lastPayment.getTime())) {
    return { nextBillingText: 'Invalid date', nextBillingClass: 'text-red-600' }
  }

  const nextBillingDate = new Date(lastPayment)
  nextBillingDate.setDate(nextBillingDate.getDate() + 30)

  const today = new Date()
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  )
  const startOfNextBilling = new Date(
    nextBillingDate.getFullYear(),
    nextBillingDate.getMonth(),
    nextBillingDate.getDate(),
  )

  const msInDay = 1000 * 60 * 60 * 24
  const diffDays = Math.floor(
    (startOfNextBilling.getTime() - startOfToday.getTime()) / msInDay,
  )

  let nextBillingClass = ''
  if (diffDays <= 0) {
    nextBillingClass = 'text-red-600'
  } else if (diffDays < 7) {
    nextBillingClass = 'text-orange-500'
  }

  return {
    nextBillingText: nextBillingDate.toLocaleDateString(),
    nextBillingClass,
  }
}

/**
 * Load users from backend
 *
 * T085: Wire up listUsers callable in user-list.ts
 */
async function loadUsers(
  options: {
    pageToken?: string
    searchEmail?: string
    filterByPlanType?: 'free' | 'standard' | 'enterprise'
  } = {},
): Promise<void> {
  console.log('[User List] Loading users with options:', options)

  const container = document.getElementById('user-list-container')
  if (!container) {
    console.error('[User List] Container not found')
    return
  }

  // Show loading state
  container.innerHTML = `
    <div class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      <span class="ml-3 text-gray-600">Loading users...</span>
    </div>
  `

  try {
    // Call listUsers callable
    const listUsersCallable = httpsCallable<any, ListUsersResponse>(
      functions,
      'listUsers',
    )

    const result = await listUsersCallable({
      pageSize: 20,
      pageToken: options.pageToken,
      searchEmail: options.searchEmail || undefined,
      filterByPlanType: options.filterByPlanType || undefined,
    })

    const data = result.data
    currentUsers = data.users
    currentPageToken = data.nextPageToken

    console.log('[User List] Loaded users:', {
      count: data.totalCount,
      hasNextPage: !!data.nextPageToken,
    })

    // Render users table
    renderUsersTable(data.users)

    // Render pagination
    renderPagination(data.nextPageToken)
  } catch (error: any) {
    console.error('[User List] Error loading users:', error)
    container.innerHTML = `
      <div class="text-center py-12">
        <div class="text-red-600 font-medium mb-2">Error Loading Users</div>
        <div class="text-gray-600 text-sm">${
          error.message || 'Unknown error occurred'
        }</div>
        <button
          onclick="window.location.reload()"
          class="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    `
  }
}

/**
 * Render users table
 */
function renderUsersTable(users: UserData[]): void {
  const container = document.getElementById('user-list-container')
  if (!container) return

  if (users.length === 0) {
    container.innerHTML = `
      <div class="text-center py-12 text-gray-500">
        No users found. Try adjusting your filters.
      </div>
    `
    return
  }

  const tableHTML = `
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Plan Type
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Payment At
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Branches
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Next Billing At
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created At
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          ${users.map((user) => renderUserRow(user)).join('')}
        </tbody>
      </table>
    </div>
  `

  container.innerHTML = tableHTML

  // Add event listeners for action buttons
  users.forEach((user) => {
    const viewBtn = document.getElementById(`view-user-${user.uid}`)
    const editBtn = document.getElementById(`edit-subscription-${user.uid}`)
    const manageBranchesBtn = document.getElementById(
      `manage-branches-${user.uid}`,
    )
    const generateBranchCodeBtn = document.getElementById(
      `generate-branch-code-${user.uid}`,
    )
    const generateEntCodeBtn = document.getElementById(
      `generate-ent-code-${user.uid}`,
    )

    viewBtn?.addEventListener('click', () => viewUserDetails(user))
    editBtn?.addEventListener('click', () => editUserSubscription(user))
    manageBranchesBtn?.addEventListener('click', () => {
      showManageBranchesModal({
        uid: user.uid,
        email: user.email,
      })
    })

    // Existing handler for BRANCH codes
    generateBranchCodeBtn?.addEventListener('click', async (e) => {
      const button = e.currentTarget as HTMLButtonElement
      const originalText = button.textContent
      button.textContent = '...'
      button.disabled = true

      try {
        const accountId = user.accounts[0]?.id
        if (!accountId) {
          throw new Error('This user has no associated account.')
        }

        const generateCode = httpsCallable(
          functions,
          'adminGenerateOneTimeCode',
        )
        const result = await generateCode({ accountId })
        const data = result.data as {
          success: boolean
          code: string
          expiresAt: string
        }

        if (data.success && data.code) {
          // --- [THIS IS THE FIX] ---
          // OLD: alert(...)
          // NEW: Call the modal function
          showGeneratedCodeModal(data.code, data.expiresAt)
          // --- [END FIX] ---
        }
      } catch (error: any) {
        console.error('Error generating one-time code:', error)
        alert(`Error: ${error.message || 'Could not generate code.'}`)
      } finally {
        button.textContent = originalText
        button.disabled = false
      }
    })

    // NEW event listener for ENTERPRISE codes
    generateEntCodeBtn?.addEventListener('click', async (e) => {
      const button = e.currentTarget as HTMLButtonElement
      const originalText = button.textContent
      button.textContent = '...'
      button.disabled = true

      try {
        const accountId = user.accounts[0]?.id
        if (!accountId) {
          throw new Error('This user has no associated account.')
        }

        // Call the NEW cloud function
        const generateCode = httpsCallable(
          functions,
          'adminGenerateEnterpriseCode',
        )
        const result = await generateCode({ accountId })
        const data = result.data as {
          success: boolean
          code: string
          expiresAt: string
        }

        if (data.success && data.code) {
          // --- [THIS IS THE FIX] ---
          // OLD: alert(...)
          // NEW: Call the modal function
          showGeneratedCodeModal(data.code, data.expiresAt)
          // --- [END FIX] ---
        }
      } catch (error: any) {
        console.error('Error generating enterprise code:', error)
        alert(`Error: ${error.message || 'Could not generate code.'}`)
      } finally {
        button.textContent = originalText
        button.disabled = false
      }
    })
  })
}

/**
 * Render a single user row
 */
function renderUserRow(user: UserData): string {
  const planBadgeColor =
    user.planType === 'enterprise'
      ? 'bg-purple-100 text-purple-800'
      : user.planType === 'standard'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-gray-100 text-gray-800'

  const payAtText = user.payAt ? new Date(user.payAt).toLocaleDateString() : '—'
  const renamedBranches = user.branchStats?.renamed ?? 0
  const totalBranches = user.branchStats?.total ?? 0
  const branchSummary = `${renamedBranches} / ${totalBranches}`
  const branchTooltip = `Current used: ${renamedBranches} branch(es)\nTotal bought: ${totalBranches} branch(es)`

  const { nextBillingText, nextBillingClass } = getNextBillingDetails(user.payAt)

  const hasAccount = user.accounts.length > 0
  const generateCodeBtnDisabled = !hasAccount ? 'disabled' : ''
  const generateCodeBtnClass = !hasAccount
    ? 'text-gray-400 cursor-not-allowed'
    : 'text-teal-600 hover:text-teal-900'

  const isEnterprise = user.planType === 'enterprise'
  const genEntCodeDisabled = !hasAccount || isEnterprise ? 'disabled' : ''
  const genEntCodeClass =
    !hasAccount || isEnterprise
      ? 'text-gray-400 cursor-not-allowed'
      : 'text-purple-600 hover:text-purple-900'
  const genEntCodeTitle = isEnterprise
    ? 'User is already Enterprise'
    : !hasAccount
      ? 'User has no account yet'
      : 'Generate 5-min Enterprise code'

  return `
    <tr class="hover:bg-gray-50">
      <td class="px-6 py-4 whitespace-nowrap">
        <div class="text-sm font-medium text-gray-900">${
          user.email || 'No email'
        }</div>
        <div class="text-sm text-gray-500">${user.displayName || ''}</div>
      </td>
      <td class="px-6 py-4 whitespace-nowrap">
        <span class="px-2 py-1 text-xs font-medium rounded-full ${planBadgeColor}">
          ${user.planType}
        </span>
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        ${payAtText}
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <span class="tooltip-wrapper">
          ${branchSummary}
          <span class="tooltip-content">Current used: ${renamedBranches} branch(es)<br/>Total bought: ${totalBranches} branch(es)</span>
        </span>
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium ${nextBillingClass}">
        ${nextBillingText}
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        ${new Date(user.createdAt).toLocaleDateString()}
      </td>
      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
        <button
          id="view-user-${user.uid}"
          class="text-indigo-600 hover:text-indigo-900"
        >
          View
        </button>
        <button
          id="manage-branches-${user.uid}"
          class="text-blue-600 hover:text-blue-900"
        >
          Branches
        </button>
        <button
          id="edit-subscription-${user.uid}"
          class="text-green-600 hover:text-green-900"
        >
          Edit
        </button>
        <button
          id="generate-branch-code-${user.uid}"
          class="${generateCodeBtnClass}"
          ${generateCodeBtnDisabled}
          title="${
            !hasAccount
              ? 'User has no account yet'
              : 'Generate 5-min branch code'
          }"
        >
          Gen. Branch Code
        </button>
        <button
          id="generate-ent-code-${user.uid}"
          class="generate-ent-code-btn ${genEntCodeClass}"
          ${genEntCodeDisabled}
          title="${genEntCodeTitle}"
        >
          Gen. Ent. Code
        </button>
      </td>
    </tr>
  `
}

/**
 * Render pagination controls
 */
function renderPagination(nextPageToken: string | null): void {
  const container = document.getElementById('pagination-container')
  if (!container) return

  if (!nextPageToken) {
    container.innerHTML = `
      <div class="text-sm text-gray-500">
        Showing ${currentUsers.length} user(s)
      </div>
    `
    return
  }

  container.innerHTML = `
    <div class="text-sm text-gray-500">
      Showing ${currentUsers.length} user(s)
    </div>
    <button
      id="load-more-btn"
      class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
    >
      Load More
    </button>
  `

  const loadMoreBtn = document.getElementById('load-more-btn')
  loadMoreBtn?.addEventListener('click', () => {
    loadUsers({ pageToken: nextPageToken })
  })
}

/**
 * View user details (show modal with full info)
 */
function viewUserDetails(user: UserData): void {
  const payAtText = user.payAt
    ? new Date(user.payAt).toLocaleString()
    : 'Not available'
  const branchSummary = `${user.branchStats?.renamed ?? 0} / ${
    user.branchStats?.total ?? 0
  }`
  const nextBilling = getNextBillingDetails(user.payAt).nextBillingText

  const modal = document.createElement('div')
  modal.className =
    'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  modal.style.backdropFilter = 'blur(4px)'

  modal.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 overflow-hidden">
      <div class="p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl font-bold text-gray-900">User Details</h2>
          <button
            id="close-details-modal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <h3 class="text-sm font-medium text-gray-500">Email</h3>
            <p class="mt-1 text-sm text-gray-900">${user.email || 'N/A'}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">User ID</h3>
            <p class="mt-1 text-sm text-gray-900 font-mono">${user.uid}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Plan Type</h3>
            <p class="mt-1 text-sm text-gray-900">${user.planType}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Pay At</h3>
            <p class="mt-1 text-sm text-gray-900">${payAtText}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Next Billing</h3>
            <p class="mt-1 text-sm text-gray-900">${nextBilling}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Branches</h3>
            <p class="mt-1 text-sm text-gray-900">${branchSummary} (renamed / total)</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Accounts</h3>
            <p class="mt-1 text-sm text-gray-900">${
              user.accounts.length
            } account(s)</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Subscriptions</h3>
            <p class="mt-1 text-sm text-gray-900">${
              user.subscriptionCount
            } subscription(s)</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Created At</h3>
            <p class="mt-1 text-sm text-gray-900">${new Date(
              user.createdAt,
            ).toLocaleString()}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Last Sign In</h3>
            <p class="mt-1 text-sm text-gray-900">${
              user.lastSignInAt
                ? new Date(user.lastSignInAt).toLocaleString()
                : 'Never'
            }</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">Custom Claims</h3>
            <pre class="mt-1 text-xs text-gray-900 bg-gray-50 p-2 rounded overflow-x-auto">${JSON.stringify(
              user.customClaims,
              null,
              2,
            )}</pre>
          </div>
        </div>

        <div class="mt-6 flex justify-end">
          <button
            id="close-details-btn"
            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  `

  document.body.appendChild(modal)

  const closeModal = () => {
    modal.remove()
  }

  modal
    .querySelector('#close-details-modal')
    ?.addEventListener('click', closeModal)
  modal
    .querySelector('#close-details-btn')
    ?.addEventListener('click', closeModal)
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal()
    }
  })
}

/**
 * Edit user subscription (show subscription editor)
 */
function editUserSubscription(user: UserData): void {
  console.log('[User List] Opening subscription editor for user:', user.uid)
  showSubscriptionEditor(user)
}

/**
 * Initialize user list component
 */
export function initializeUserList(): void {
  console.log('[User List] Initializing')

  // Load initial users
  loadUsers()

  document.addEventListener('admin-user-branches-updated', () => {
    loadUsers()
  })

  // Setup search button
  const searchBtn = document.getElementById('search-users-btn')
  searchBtn?.addEventListener('click', () => {
    const emailInput = document.getElementById(
      'search-email',
    ) as HTMLInputElement
    const planTypeSelect = document.getElementById(
      'filter-plan-type',
    ) as HTMLSelectElement

    loadUsers({
      searchEmail: emailInput?.value?.trim() || undefined,
      filterByPlanType: (planTypeSelect?.value as any) || undefined,
    })
  })

  // Setup enter key on search input
  const emailInput = document.getElementById('search-email') as HTMLInputElement
  emailInput?.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      searchBtn?.click()
    }
  })
}

export function refreshUserList(): void {
  loadUsers()
}